import React, { useState, useEffect } from 'react';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/Table';
import { useAuth } from '../contexts/AuthContext';
import MainLayout from '../layouts/MainLayout';
import ApiService from '../services/api';
import Autocomplete from '../components/ui/Autocomplete';
import { Download, Filter, RefreshCw, Users, Phone, Clock, CheckCircle } from 'lucide-react';

interface Agent {
  id: number;
  name: string;
  email: string;
  username: string;
}

interface AgentCallCount {
  agent_id: number;
  agent_name: string;
  agent_email: string;
  agent_username: string;
  total_calls: number;
  completed_calls: number;
  completion_rate: number;
  avg_duration: number;
  total_duration: number;
}

const today = new Date();
const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
const formatDateInput = (date: Date) => date.toISOString().slice(0, 10);

const AgentCallCountReport: React.FC = () => {
  const { token } = useAuth();
  const [report, setReport] = useState<AgentCallCount[]>([]);
  const [loading, setLoading] = useState(false);
  const [dateFrom, setDateFrom] = useState(formatDateInput(firstDayOfMonth));
  const [dateTo, setDateTo] = useState(formatDateInput(today));
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
  const [agentSearchValue, setAgentSearchValue] = useState('');

  const searchAgents = async (query: string): Promise<Agent[]> => {
    if (!token) return [];

    try {
      const response = await ApiService.get<Agent[]>('agent-performance-report/search-agents', { query });
      return response.data;
    } catch (error) {
      console.error('Error searching agents:', error);
      return [];
    }
  };

  const handleAgentSelect = (option: any) => {
    if (option) {
      setSelectedAgent({
        id: option.id,
        name: option.name,
        email: option.email,
        username: option.username || ''
      });
    } else {
      setSelectedAgent(null);
    }
  };

  const fetchReport = async () => {
    if (!token) return;

    setLoading(true);
    try {
      const params: Record<string, any> = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (selectedAgent) params.agent_id = selectedAgent.id;

      const response = await ApiService.get<AgentCallCount[]>('agent-performance-report/agent-call-count', params);
      setReport(response.data);
    } catch (error) {
      console.error('Error fetching agent call count report:', error);
    } finally {
      setLoading(false);
    }
  };

  const exportReport = async (format: 'csv' = 'csv') => {
    if (!token) return;

    try {
      const params: Record<string, any> = { format };
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (selectedAgent) params.agent_id = selectedAgent.id;

      const response = await ApiService.get('agent-performance-report/export-call-report', params);
      const data = response.data as { data: any[]; filename: string; format: string };

      if (format === 'csv') {
        const csvContent = data.data.map((row: any[]) => row.join(',')).join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = data.filename;
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error exporting report:', error);
    }
  };

  useEffect(() => {
    fetchReport();
  }, [token, dateFrom, dateTo, selectedAgent]);

  const formatDuration = (seconds: number) => {
    if (!seconds || seconds === 0) return '-';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    if (hours > 0) {
      return `${hours}h ${minutes}m ${secs}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  };

  const getCompletionRateColor = (rate: number) => {
    if (rate >= 80) return 'text-green-600';
    if (rate >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const calculateAverageCompletionRate = () => {
    if (report.length === 0) return 0;
    
    // Filter out agents with invalid completion rates and convert to numbers
    const validRates = report
      .filter(agent => {
        const rate = parseFloat(agent.completion_rate as any);
        return !isNaN(rate) && isFinite(rate);
      })
      .map(agent => parseFloat(agent.completion_rate as any));
    
    if (validRates.length === 0) return 0;
    
    const totalRate = validRates.reduce((sum, rate) => sum + rate, 0);
    return Math.round(totalRate / validRates.length);
  };

  const calculateTotalDuration = () => {
    const total = report.reduce((sum, agent) => {
      const duration = parseFloat(agent.total_duration as any);
      return sum + (isNaN(duration) ? 0 : duration);
    }, 0);
    return Math.floor(total); // Remove decimal places
  };

  return (
    <MainLayout>
      <div className="p-6">
        {/* Page Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Agent Call Count Report
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            View agent list with call count, completion rates, and performance metrics
          </p>
        </div>

        {/* Filters */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 mb-6">
          <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center w-full lg:w-auto">
              <div className="flex items-center gap-2">
                <Filter className="w-5 h-5 text-gray-500" />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Filters:</span>
              </div>
              <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
                <Input
                  type="date"
                  value={dateFrom}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDateFrom(e.target.value)}
                  className="w-full sm:w-40"
                  placeholder="From Date"
                />
                <Input
                  type="date"
                  value={dateTo}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDateTo(e.target.value)}
                  className="w-full sm:w-40"
                  placeholder="To Date"
                />
                <Autocomplete<Agent>
                  placeholder="Search agents..."
                  value={agentSearchValue}
                  onChange={setAgentSearchValue}
                  onSelect={(agent) => setSelectedAgent(agent)}
                  searchFunction={searchAgents}
                  debounceMs={300}
                  className="w-full"
                  style={{ minWidth: '200px' }}
                  displayKey="name"
                  subDisplayKey="email"
                />
              </div>
            </div>
            <div className="flex gap-2 w-full lg:w-auto">
              <Button onClick={fetchReport} disabled={loading} className="flex items-center gap-2 w-full sm:w-auto">
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                {loading ? 'Loading...' : 'Refresh'}
              </Button>
              <Button onClick={() => exportReport('csv')} className="flex items-center gap-2 w-full sm:w-auto">
                <Download className="w-4 h-4" />
                Export CSV
              </Button>
            </div>
          </div>
        </div>

        {/* Summary Stats */}
        {report.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
              <div className="flex items-center">
                <Users className="w-8 h-8 text-blue-500 mr-3" />
                <div>
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Agents</h3>
                  <div className="text-2xl font-bold text-gray-900 dark:text-white">{report.length}</div>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
              <div className="flex items-center">
                <Phone className="w-8 h-8 text-green-500 mr-3" />
                <div>
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Calls</h3>
                  <div className="text-2xl font-bold text-blue-600">
                    {report.reduce((sum, agent) => sum + agent.total_calls, 0)}
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
              <div className="flex items-center">
                <CheckCircle className="w-8 h-8 text-purple-500 mr-3" />
                <div>
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Avg Completion Rate</h3>
                  <div className="text-2xl font-bold text-green-600">
                    {calculateAverageCompletionRate()}%
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
              <div className="flex items-center">
                <Clock className="w-8 h-8 text-orange-500 mr-3" />
                <div>
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Duration</h3>
                  <div className="text-2xl font-bold text-gray-900 dark:text-white">
                    {formatDuration(calculateTotalDuration())}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Report Table */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Agent Call Count Report</h2>
          </div>
          <div className="overflow-x-auto">
            {loading ? (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4" />
                Loading report data...
              </div>
            ) : report.length === 0 ? (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">No data available</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Agent Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Username</TableHead>
                    <TableHead>Total Calls</TableHead>
                    <TableHead>Completed</TableHead>
                    <TableHead>Completion Rate</TableHead>
                    <TableHead>Avg Duration</TableHead>
                    <TableHead>Total Duration</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {report.map((agent) => (
                    <TableRow key={agent.agent_id}>
                      <TableCell className="font-medium">{agent.agent_name}</TableCell>
                      <TableCell>{agent.agent_email}</TableCell>
                      <TableCell>{agent.agent_username}</TableCell>
                      <TableCell className="font-semibold">{agent.total_calls}</TableCell>
                      <TableCell className="text-green-600">{agent.completed_calls}</TableCell>
                      <TableCell>
                        <span className={getCompletionRateColor(agent.completion_rate || 0)}>
                          {(() => {
                            const rate = parseFloat(agent.completion_rate as any);
                            return isNaN(rate) ? 0 : Math.round(rate);
                          })()}%
                        </span>
                      </TableCell>
                      <TableCell>{formatDuration(parseFloat(agent.avg_duration as any) || 0)}</TableCell>
                      <TableCell>{formatDuration(parseFloat(agent.total_duration as any) || 0)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default AgentCallCountReport; 